import { useState } from 'react';
import { Plus, Bell, Loader2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  TabFilter,
} from '@travellegacy/nexops-design-system';
import { useReminders, useReminderStats } from '../hooks/useReminders';
import { ReminderCard } from '../components/ReminderCard';
import { ReminderForm } from '../components/ReminderForm';
import type { Reminder } from '../services/reminders.service';

export function RemindersListPage() {
  const [statusFilter, setStatusFilter] = useState<Reminder['status'] | 'ALL'>('ALL');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data: reminders, isLoading, error } = useReminders(
    statusFilter === 'ALL' ? undefined : statusFilter
  );
  const { data: stats } = useReminderStats();

  const STATUS_TABS = [
    { key: 'ALL', label: 'Todos', count: stats ? Object.values(stats).reduce((a, b) => a + b, 0) : 0 },
    { key: 'PENDING' as const, label: 'Pendentes', count: stats?.PENDING || 0 },
    { key: 'FAILED' as const, label: 'Falharam', count: stats?.FAILED || 0 },
    { key: 'CANCELLED' as const, label: 'Cancelados', count: stats?.CANCELLED || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Lembretes</h1>
          <p className="text-muted-foreground mt-2">
            Gerencie seus lembretes e receba notificações via WhatsApp e Slack
          </p>
        </div>

        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Lembrete
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Pendentes</CardDescription>
              <CardTitle className="text-3xl text-yellow-600">{stats.PENDING}</CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Falharam</CardDescription>
              <CardTitle className="text-3xl text-red-600">{stats.FAILED}</CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Cancelados</CardDescription>
              <CardTitle className="text-3xl text-gray-600">{stats.CANCELLED}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Dialog de Criação */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Criar Novo Lembrete</DialogTitle>
            <DialogDescription>
              Preencha os dados abaixo para criar um novo lembrete
            </DialogDescription>
          </DialogHeader>
          <div className="px-4 sm:px-6 py-4">
            <ReminderForm
              onSuccess={() => setShowCreateDialog(false)}
              onCancel={() => setShowCreateDialog(false)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Filtros */}
      <div>
        <TabFilter
          tabs={STATUS_TABS.map((tab) => ({
            value: tab.key,
            label: `${tab.label} (${tab.count})`,
          }))}
          currentTab={statusFilter}
          onChange={(value) => setStatusFilter(value as Reminder['status'] | 'ALL')}
        />
      </div>

      {/* Lista de Lembretes */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-destructive">
              <p>Erro ao carregar lembretes. Tente novamente.</p>
            </div>
          </CardContent>
        </Card>
      ) : !reminders || reminders.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Bell className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Nenhum lembrete encontrado</h3>
              <p className="text-muted-foreground mb-6">
                {statusFilter === 'ALL'
                  ? 'Crie seu primeiro lembrete para começar!'
                  : `Não há lembretes com status "${STATUS_TABS.find((t) => t.key === statusFilter)?.label}"`}
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Lembrete
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reminders.map((reminder) => (
            <ReminderCard key={reminder.id} reminder={reminder} />
          ))}
        </div>
      )}
    </div>
  );
}
